import { Typography } from '@mui/material';
import {
  CreateWorkflowNode,
  NodeValidationExtraData,
  UnprocessableEntityErrorResponse,
  WorkflowNodeType,
} from 'api/model';
import { getNodeConfig } from 'api/workflows-steps/formSchema';
import { validateNode } from 'api/workflows/requests';
import { isAxiosError } from 'axios';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';

export const nodeErrorToast = (
  formData: CreateWorkflowNode,
  message: string,
  color: string,
  icon: React.ReactNode,
  location: string | null = null,
) => {
  toast.error(
    <>
      <div
        className="absolute flex items-center justify-center p-1 rounded top-1/2 -left-5 -translate-y-1/2"
        style={{
          backgroundColor: color,
        }}
      >
        {icon}
      </div>
      <Typography variant="subtitle2">{formData.name}</Typography>
      <Typography variant="caption">
        {location && `${location} - `}
        {message}
      </Typography>
    </>,
    {
      icon: false,
      className: 'overflow-visible gap-1 relative ring-1 ring-red-500/50',
    },
  );
};

export const useNodeValidation = (type: WorkflowNodeType) => {
  const { projectId = '' } = useParams();
  const { color, icon } = getNodeConfig(type)!;

  const validate = async (formData: CreateWorkflowNode, extraData: NodeValidationExtraData = null) => {
    try {
      const result = await validateNode(formData, projectId, extraData);
      if (!result.valid) {
        result.errors.forEach((error) => nodeErrorToast(formData, error, color, icon));
        return false;
      }
    } catch (error) {
      if (isAxiosError<UnprocessableEntityErrorResponse>(error) && error.status === 422) {
        error.response?.data.detail.forEach((error) =>
          nodeErrorToast(formData, error.msg, color, icon, error.loc.at(-1) as string),
        );
        return false;
      }
      toast.error('Node validation failed.');
      return false;
    }
    return true;
  };

  return { validate };
};
